import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { all_routes } from "../../router/all_routes";
import { getEvLoan, updateEvLoanStatus } from "../../../apis/evLoans";
import { uploadFile } from "../../../apis/auth";
import { formatEvMoney } from "./evLoanFormatting";

const money = formatEvMoney;
const requestError = (error) => error?.response?.data?.message || error?.message || "Request failed";

export default function EvLoanDisbursementPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [form, setForm] = useState({ disbursement_date: "", disbursement_mode: "bank", disbursement_reference: "",
    first_due_date: "", disbursement_proof_url: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");
  const userType = JSON.parse(sessionStorage.getItem("info") || "{}").type;
  const recordPath = all_routes.evLoanRecord.replace(":id", id);
  const netDisbursement = Number(loan?.principal || 0);

  useEffect(() => {
    setLoading(true);
    getEvLoan(id).then((response) => {
      setLoan(response.data);
      setForm((previous) => ({ ...previous, first_due_date: response.data.first_due_date || "" }));
    })
      .catch((failure) => setError(requestError(failure))).finally(() => setLoading(false));
  }, [id]);

  async function submit(event) {
    event.preventDefault();
    setConfirmOpen(true);
  }

  async function confirmDisbursement() {
    setBusy(true); setError("");
    try {
      await updateEvLoanStatus(id, { status: "disbursed", ...form });
      setConfirmOpen(false);
      navigate(recordPath, { replace: true, state: { from: all_routes.evCustomerLoans, message: "Loan disbursed successfully" } });
    } catch (failure) { setError(requestError(failure)); setBusy(false); }
  }

  async function returnToPending() {
    setBusy(true); setError("");
    try {
      await updateEvLoanStatus(id, { status: "pending" });
      navigate(all_routes.evCustomerLoans, { replace: true });
    } catch (failure) { setError(requestError(failure)); setBusy(false); }
  }

  async function uploadProof(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    if (file.size > 10 * 1024 * 1024 || !(file.type === "application/pdf" || file.type.startsWith("image/"))) {
      setError("Choose a PDF or image smaller than 10 MB."); return;
    }
    setBusy(true); setError("");
    try {
      const data = new FormData(); data.append("image", file);
      const response = await uploadFile(data);
      if (!response.status || !response.data?.image) throw new Error("Upload failed");
      setForm((previous) => ({ ...previous, disbursement_proof_url: response.data.image }));
    } catch (failure) { setError(requestError(failure)); }
    finally { setBusy(false); }
  }

  return <div className="page-wrapper modern-ui"><div className="content container-fluid">
    <Link className="btn btn-link ps-0 mb-2" to={all_routes.evCustomerLoans}><i className="ti ti-arrow-left me-1" /> Back to customer loans</Link>
    <h3 className="mb-3">EV loan disbursement</h3>
    {loading && <p role="status">Loading loan...</p>}
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {!loading && loan && <>
      <div className="card shadow-sm mb-4"><div className="card-header">Review approved loan #{loan.id}</div><div className="card-body">
        <div className="row g-3">
          <div className="col-md-4"><span className="text-muted d-block">Customer</span><strong>{loan.customer?.full_name || "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Loan account</span><strong>{loan.loan_account_number || `VasudevEV${String(loan.id).padStart(6, "0")}`}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Status</span><strong className="text-capitalize">{loan.status}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Product and asset</span><strong>{loan.product?.name || "EV loan"} · {loan.asset_description}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Purchase price</span><strong>{money(loan.price)}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Down payment</span><strong>{money(loan.down_payment)}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Gross disbursement</span><strong>{money(loan.principal)}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Monthly EMI</span><strong>{money(loan.monthly_emi)} for {loan.term_months} months</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">First EMI due</span><strong>{loan.first_due_date}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Processing fee collected separately</span><strong>{money(loan.processing_charges)}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">File charges collected separately</span><strong>{money(loan.file_charges)}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Other charges collected separately</span><strong>{money(loan.other_charges)}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Charges received</span><strong>{loan.charges_payment_mode ? `${loan.charges_payment_mode} · ${loan.charges_payment_date}` : "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Net amount to be disbursed</span><strong>{money(loan.principal)}</strong></div>
          <div className="col-12"><h5 className="mb-0">Dealer transfer details</h5></div>
          <div className="col-md-4"><span className="text-muted d-block">Dealer</span><strong>{loan.dealer_details?.name || "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Dealer phone</span><strong>{loan.dealer_details?.phone || "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Dealer address</span><strong>{loan.dealer_details?.address || "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Bank</span><strong>{loan.dealer_details?.bank_name || "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Branch</span><strong>{loan.dealer_details?.bank_branch || "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Account holder</span><strong>{loan.dealer_details?.account_name || "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">Account number</span><strong>{loan.dealer_details?.account_number || "—"}</strong></div>
          <div className="col-md-4"><span className="text-muted d-block">IFSC</span><strong>{loan.dealer_details?.ifsc_code || "—"}</strong></div>
        </div>
        {loan.decision_note && <p className="mt-3 mb-0 text-muted">Approval note: {loan.decision_note}</p>}
        <Link className="btn btn-outline-primary mt-3" to={recordPath}>View EMI schedule</Link>
      </div></div>
      {loan.status === "approved" && userType === "financer" && <form className="card shadow-sm mb-4" onSubmit={submit}>
        <div className="card-header">Record disbursement</div><div className="card-body">
          <p className="text-muted">Confirm the transfer details before marking this loan as disbursed.</p>
          <div className="row g-3">
            <div className="col-md-4"><label className="form-label" htmlFor="ev_disbursement_date">Disbursement date</label>
              <input id="ev_disbursement_date" className="form-control" type="date" max={form.first_due_date || undefined} required value={form.disbursement_date}
                onChange={(event) => setForm({ ...form, disbursement_date: event.target.value })} /></div>
            <div className="col-md-4"><label className="form-label" htmlFor="ev_first_emi_date">EMI start date / first EMI due</label>
              <input id="ev_first_emi_date" className="form-control" type="date" min={form.disbursement_date || undefined} required value={form.first_due_date}
                onChange={(event) => setForm({ ...form, first_due_date: event.target.value })} /></div>
            <div className="col-md-4"><label className="form-label" htmlFor="ev_disbursement_mode">Payment mode</label>
              <select id="ev_disbursement_mode" className="form-select" value={form.disbursement_mode} onChange={(event) => setForm({ ...form, disbursement_mode: event.target.value })}>
                <option value="bank">Bank</option><option value="upi">UPI</option><option value="cash">Cash</option>
              </select></div>
            <div className="col-md-4"><label className="form-label" htmlFor="ev_disbursement_reference">Transaction reference</label>
              <input id="ev_disbursement_reference" className="form-control" required value={form.disbursement_reference}
                onChange={(event) => setForm({ ...form, disbursement_reference: event.target.value })} /></div>
            <div className="col-md-4"><label className="form-label" htmlFor="ev_disbursement_proof">Transfer proof</label>
              <input id="ev_disbursement_proof" className="form-control" type="file" accept="image/*,.pdf" disabled={busy} onChange={uploadProof} />
              {form.disbursement_proof_url && <a href={form.disbursement_proof_url} target="_blank" rel="noopener noreferrer">View proof</a>}
            </div>
          </div>
          <p className="mt-3 mb-0 fw-bold">Charges were collected separately; full principal will be disbursed: {money(netDisbursement)}</p>
          <div className="d-flex flex-wrap gap-2 mt-4">
            <button className="btn btn-primary" disabled={busy || !form.disbursement_reference.trim() ||
              !form.disbursement_date || !form.first_due_date || form.first_due_date < form.disbursement_date}>Review disbursement</button>
            <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={returnToPending}>Return to pending for changes</button>
          </div>
        </div>
      </form>}
      {loan.status !== "approved" && <div className="alert alert-info">This loan is {loan.status}; disbursement can only be recorded for an approved loan.</div>}
      {loan.status === "approved" && userType !== "financer" && <div className="alert alert-info">A financer must record the disbursement.</div>}
    </>}
    <Modal show={confirmOpen && !!loan} onHide={() => { if (!busy) setConfirmOpen(false); }} centered>
      <Modal.Header closeButton><Modal.Title>Confirm EV loan disbursement</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        <p>Review the transfer before recording it for <strong>{loan?.customer?.full_name}</strong>.</p>
        <dl className="row mb-0"><dt className="col-5">Loan account</dt><dd className="col-7">{loan?.loan_account_number}</dd>
          <dt className="col-5">Date</dt><dd className="col-7">{form.disbursement_date}</dd>
          <dt className="col-5">First EMI due</dt><dd className="col-7">{form.first_due_date}</dd>
          <dt className="col-5">Mode</dt><dd className="col-7 text-capitalize">{form.disbursement_mode}</dd>
          <dt className="col-5">Reference</dt><dd className="col-7">{form.disbursement_reference}</dd>
          <dt className="col-5">Gross amount</dt><dd className="col-7">{money(loan?.principal)}</dd>
          <dt className="col-5">Processing fee collected separately</dt><dd className="col-7">{money(loan?.processing_charges)}</dd>
          <dt className="col-5">File charges collected separately</dt><dd className="col-7">{money(loan?.file_charges)}</dd>
          <dt className="col-5">Other charges collected separately</dt><dd className="col-7">{money(loan?.other_charges)}</dd>
          <dt className="col-5">Dealer</dt><dd className="col-7">{loan?.dealer_details?.name || "—"}</dd>
          <dt className="col-5">Bank</dt><dd className="col-7">{loan?.dealer_details?.bank_name || "—"}</dd>
          <dt className="col-5">Account holder</dt><dd className="col-7">{loan?.dealer_details?.account_name || "—"}</dd>
          <dt className="col-5">Account number</dt><dd className="col-7">{loan?.dealer_details?.account_number || "—"}</dd>
          <dt className="col-5">IFSC</dt><dd className="col-7">{loan?.dealer_details?.ifsc_code || "—"}</dd>
          <dt className="col-5">Net amount</dt><dd className="col-7 fw-bold">{money(loan?.principal)}</dd>
        </dl>
      </Modal.Body>
      <Modal.Footer><button className="btn btn-outline-secondary" disabled={busy} onClick={() => setConfirmOpen(false)}>Back</button>
        <button className="btn btn-primary" disabled={busy} onClick={confirmDisbursement}>Confirm disbursement</button></Modal.Footer>
    </Modal>
  </div></div>;
}
